import { FormEvent, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authApi } from '../../api';
import { getErrorMessage } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type Feature = {
  icon: string;
  title: string;
  desc: string;
  bg: string;
  accent: string;
};

type Highlight = {
  icon: string;
  title: string;
  desc: string;
  color: string;
};

type DemoAccount = {
  email: string;
  password: string;
  role: string;
  scope: string;
  initial: string;
  color: string;
  bg: string;
};

const FEATURES: Feature[] = [
  {
    icon: '🖥️',
    title: 'POS & Shifts',
    desc: 'Touch-optimised register, shift management, barcode scanning, and split payments.',
    bg: 'linear-gradient(180deg, rgba(9, 35, 28, 0.96) 0%, rgba(5, 18, 15, 0.98) 100%)',
    accent: '#10B981',
  },
  {
    icon: '📦',
    title: 'Inventory',
    desc: 'Real-time stock across warehouses, serial & batch tracking, and reorder visibility.',
    bg: 'linear-gradient(180deg, rgba(11, 28, 50, 0.96) 0%, rgba(7, 17, 30, 0.98) 100%)',
    accent: '#3B82F6',
  },
  {
    icon: '🛒',
    title: 'Purchasing',
    desc: 'Full procure-to-pay flow from RFQ to GRN with vendor invoice matching.',
    bg: 'linear-gradient(180deg, rgba(48, 28, 4, 0.96) 0%, rgba(24, 13, 2, 0.98) 100%)',
    accent: '#F59E0B',
  },
  {
    icon: '📊',
    title: 'Finance & GL',
    desc: 'Double-entry GL, AR/AP, reconciliation, closing, and profitability insights.',
    bg: 'linear-gradient(180deg, rgba(38, 15, 57, 0.96) 0%, rgba(19, 7, 28, 0.98) 100%)',
    accent: '#A855F7',
  },
  {
    icon: '👥',
    title: 'HRM & Payroll',
    desc: 'Employees, attendance, leave, payroll runs, approvals, and workforce records.',
    bg: 'linear-gradient(180deg, rgba(58, 12, 26, 0.96) 0%, rgba(28, 6, 13, 0.98) 100%)',
    accent: '#F43F5E',
  },
  {
    icon: '🌐',
    title: 'eCommerce',
    desc: 'Storefront operations, product sync, promotions, reviews, and campaign tools.',
    bg: 'linear-gradient(180deg, rgba(10, 34, 35, 0.96) 0%, rgba(5, 17, 18, 0.98) 100%)',
    accent: '#14B8A6',
  },
];

const HIGHLIGHTS: Highlight[] = [
  { icon: '⚡', title: 'Real-time POS', desc: 'Sub-second checkout with offline fallback', color: '#059669' },
  { icon: '📦', title: 'Live inventory', desc: 'Multi-warehouse stock and WAC costing', color: '#2563EB' },
  { icon: '🏦', title: 'Full accounting', desc: 'GL, AR/AP, reconciliation and financial control', color: '#8B5CF6' },
  { icon: '📡', title: 'Biometric sync', desc: 'ZKTeco and Anviz machine integration', color: '#D97706' },
  { icon: '💱', title: 'Multi-currency', desc: '38 currencies with live FX rates', color: '#0F766E' },
  { icon: '📈', title: 'Reports & BI', desc: 'KPI tracking, aging, trends and profitability', color: '#DC2626' },
];

const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    email: 'admin@omniretail.local',
    password: 'admin123',
    role: 'Admin',
    scope: 'Full access',
    initial: 'A',
    color: '#047857',
    bg: '#D1FAE5',
  },
  {
    email: 'manager@omniretail.local',
    password: 'manager123',
    role: 'Manager',
    scope: 'Operations',
    initial: 'M',
    color: '#2563EB',
    bg: '#DBEAFE',
  },
  {
    email: 'cashier@omniretail.local',
    password: 'cashier123',
    role: 'Cashier',
    scope: 'POS only',
    initial: 'C',
    color: '#B45309',
    bg: '#FEF3C7',
  },
  {
    email: 'accountant@omniretail.local',
    password: 'accountant123',
    role: 'Accountant',
    scope: 'Finance',
    initial: 'A',
    color: '#7C3AED',
    bg: '#EDE9FE',
  },
];

function EmailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="9" rx="2.5" />
      <path d="M8 11V8a4 4 0 118 0v3" />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58A2 2 0 0012 16a2 2 0 001.42-.58" />
      <path d="M9.88 5.09A10.94 10.94 0 0112 5c6.5 0 10 7 10 7a18.73 18.73 0 01-4.02 4.91" />
      <path d="M6.61 6.61A18.16 18.16 0 002 12s3.5 7 10 7a10.72 10.72 0 005.09-1.28" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="M13 5l7 7-7 7" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
    </svg>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState('admin@omniretail.local');
  const [password, setPassword] = useState('admin123');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authApi.login(email, password);
      setAuth(res.user, res.accessToken, res.refreshToken);
      toast.success(`Welcome back, ${res.user.fullName}!`);
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        :root {
          --or-primary: #0f766e;
          --or-primary-strong: #0b5f59;
          --or-primary-soft: rgba(15, 118, 110, 0.1);
          --or-text: #111827;
          --or-muted: #6b7280;
          --or-line: #e5e7eb;
          --or-bg: #f7fbf9;
          --or-card: #ffffff;
        }

        * { box-sizing: border-box; }

        .or-root {
          min-height: 100vh;
          display: flex;
          background:
            radial-gradient(circle at top left, rgba(15,118,110,0.06), transparent 28%),
            linear-gradient(180deg, #f7fbf9 0%, #f3faf7 100%);
        }

        .or-left {
          display: none;
          position: relative;
          overflow: hidden;
          flex: 0 0 62%;
          min-width: 0;
          background:
            radial-gradient(circle at 14% 88%, rgba(16, 185, 129, 0.14), transparent 26%),
            radial-gradient(circle at 84% 10%, rgba(20, 184, 166, 0.09), transparent 24%),
            linear-gradient(145deg, #040c0a 0%, #071512 28%, #081b16 60%, #06100d 100%);
        }

        .or-left-glow {
          position: absolute;
          right: -90px;
          top: -40px;
          width: 360px;
          height: 360px;
          border-radius: 999px;
          box-shadow:
            0 0 0 28px rgba(255,255,255,0.02),
            0 0 0 56px rgba(255,255,255,0.014),
            0 0 0 88px rgba(255,255,255,0.008);
          border: 1px solid rgba(255,255,255,0.03);
          pointer-events: none;
        }

        .or-left-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 20px 24px 18px;
        }

        .or-brand {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          width: fit-content;
          padding: 10px 18px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          backdrop-filter: blur(8px);
        }

        .or-brand-lock {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, #12b67f 0%, #0f766e 100%);
          color: white;
          font-size: 16px;
          box-shadow: 0 8px 18px rgba(16,185,129,0.18);
        }

        .or-brand-text {
          display: flex;
          flex-direction: column;
        }

        .or-brand-title {
          color: #f9fafb;
          font-size: 20px;
          font-weight: 800;
          line-height: 1.2;
          letter-spacing: -0.03em;
        }

        .or-brand-title span {
          color: #34d399;
        }

        .or-brand-sub {
          margin-top: 4px;
          color: #64748b;
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 700;
        }

        .or-chip {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          width: fit-content;
          margin-top: 24px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          color: #cbd5e1;
          font-size: 12px;
          font-weight: 700;
        }

        .or-hero {
          margin-top: 24px;
          max-width: 560px;
        }

        .or-hero h1 {
          margin: 0;
          color: white;
          font-size: 44px;
          line-height: 1.1;
          letter-spacing: -0.04em;
          font-weight: 850;
        }

        .or-hero p {
          margin: 16px 0 0;
          color: #94a3b8;
          font-size: 14px;
          line-height: 1.6;
          max-width: 530px;
        }

        .or-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-top: 24px;
        }

        .or-stat {
          min-height: 68px;
          padding: 14px 18px;
          border-radius: 18px;
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.08);
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .or-stat-value {
          color: #ffffff;
          font-size: 28px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .or-stat-label {
          margin-top: 8px;
          color: #64748b;
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 700;
        }

        .or-feature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-top: 20px;
          flex: 1;
          align-content: start;
        }

        .or-feature {
          min-height: 120px;
          border-radius: 20px;
          padding: 16px 18px;
          border: 1px solid rgba(255,255,255,0.08);
          position: relative;
          overflow: hidden;
          transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .or-feature:hover {
          transform: translateY(-2px);
          border-color: rgba(255,255,255,0.14);
          box-shadow: 0 20px 32px rgba(0,0,0,0.2);
        }

        .or-feature::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(255,255,255,0.03), transparent 58%);
          pointer-events: none;
        }

        .or-feature-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .or-feature-icon {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.08);
          font-size: 18px;
          flex-shrink: 0;
        }

        .or-feature-arrow {
          width: 28px;
          height: 28px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.72);
          font-size: 13px;
          flex-shrink: 0;
        }

        .or-feature-title {
          position: relative;
          z-index: 1;
          margin-top: 14px;
          color: #f8fafc;
          font-size: 14px;
          font-weight: 800;
          line-height: 1.3;
        }

        .or-feature-desc {
          position: relative;
          z-index: 1;
          margin-top: 8px;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.5;
        }

        .or-left-footer {
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
        }

        .or-footer-sec {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #9ca3af;
        }

        .or-right {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 20px 24px;
        }

        .or-right-inner {
          width: 100%;
          max-width: 420px;
          margin: 0 auto;
        }

        .or-mobile-brand {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .or-mobile-brand .or-brand {
          background: rgba(255,255,255,0.78);
          border: 1px solid rgba(229,231,235,0.92);
          box-shadow: 0 12px 24px rgba(15,118,110,0.06);
        }

        .or-mobile-brand .or-brand-title {
          color: #111827;
        }

        .or-mobile-brand .or-brand-sub {
          color: #94a3b8;
        }

        .or-login-card {
          background: rgba(255,255,255,0.9);
          border: 1px solid rgba(255,255,255,0.85);
          backdrop-filter: blur(14px);
          box-shadow:
            0 20px 44px rgba(2,8,23,0.09),
            0 6px 16px rgba(2,8,23,0.04);
          border-radius: 24px;
          padding: 28px 28px 24px;
          position: relative;
          overflow: hidden;
        }

        .or-login-card::before {
          content: '';
          position: absolute;
          inset: 0 0 auto 0;
          height: 4px;
          background: linear-gradient(90deg, #10b981 0%, #0f766e 50%, #14b8a6 100%);
        }

        .or-login-brand {
          display: none;
          margin-bottom: 16px;
        }

        .or-login-brand .or-brand {
          background: #ffffff;
          border: 1px solid #edf1f4;
          box-shadow: 0 8px 18px rgba(15,118,110,0.05);
        }

        .or-login-brand .or-brand-title {
          color: #111827;
        }

        .or-login-brand .or-brand-sub {
          color: #94a3b8;
        }

        .or-login-head {
          margin-bottom: 24px;
        }

        .or-login-title {
          margin: 0;
          color: #111827;
          font-size: 32px;
          line-height: 1.2;
          font-weight: 850;
          letter-spacing: -0.04em;
        }

        .or-login-copy {
          margin: 10px 0 0;
          color: #6b7280;
          font-size: 14px;
          line-height: 1.6;
        }

        .or-error {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 18px;
          border-radius: 14px;
          padding: 12px 14px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          font-size: 13px;
          line-height: 1.5;
        }

        .or-field {
          margin-bottom: 18px;
        }

        .or-label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 8px;
        }

        .or-label {
          display: block;
          color: #374151;
          font-size: 13px;
          font-weight: 800;
        }

        .or-forgot {
          border: none;
          background: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          color: var(--or-primary);
          font-size: 12px;
          font-weight: 800;
          transition: color 0.15s ease;
        }

        .or-forgot:hover {
          color: var(--or-primary-strong);
        }

        .or-input-wrap {
          position: relative;
        }

        .or-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }

        .or-input {
          width: 100%;
          height: 48px;
          display: block;
          border: 1.5px solid #e5e7eb;
          border-radius: 14px;
          background: rgba(249,250,251,0.96);
          padding: 0 14px 0 44px;
          color: #111827;
          font-size: 14px;
          font-weight: 500;
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .or-input:hover {
          border-color: #d1d5db;
          background: #ffffff;
        }

        .or-input:focus {
          border-color: var(--or-primary);
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(15,118,110,0.1);
        }

        .or-input::placeholder {
          color: #9ca3af;
        }

        .or-input-password {
          padding-right: 48px;
        }

        .or-eye {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 0;
          transition: color 0.15s ease;
        }

        .or-eye:hover {
          color: #374151;
        }

        .or-remember-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin: 10px 0 20px;
        }

        .or-check {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }

        .or-check-box {
          width: 18px;
          height: 18px;
          border-radius: 6px;
          border: 2px solid #cbd5e1;
          background: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }

        .or-check.active .or-check-box {
          background: var(--or-primary);
          border-color: var(--or-primary);
          box-shadow: 0 4px 12px rgba(15,118,110,0.14);
        }

        .or-check-label {
          color: #374151;
          font-size: 13px;
          font-weight: 700;
        }

        .or-login-btn {
          width: 100%;
          height: 52px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(180deg, #109486 0%, #0f766e 100%);
          color: white;
          font-size: 15px;
          font-weight: 800;
          letter-spacing: 0.01em;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          cursor: pointer;
          box-shadow:
            0 12px 22px rgba(15,118,110,0.18),
            inset 0 1px 0 rgba(255,255,255,0.14);
          transition: transform 0.12s ease, box-shadow 0.15s ease;
        }

        .or-login-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow:
            0 16px 28px rgba(15,118,110,0.22),
            inset 0 1px 0 rgba(255,255,255,0.16);
        }

        .or-login-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        .or-divider {
          display: flex;
          align-items: center;
          gap: 14px;
          margin: 20px 0 16px;
        }

        .or-divider-line {
          flex: 1;
          height: 1px;
          background: #edf1f4;
        }

        .or-divider-text {
          color: #9ca3af;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .or-section-title {
          color: #111827;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }

        .or-demo-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .or-demo-card {
          border: 1px solid #edf1f4;
          background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%);
          border-radius: 16px;
          padding: 12px 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          text-align: left;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
        }

        .or-demo-card:hover {
          transform: translateY(-1px);
          border-color: #dbe2e8;
          box-shadow: 0 12px 20px rgba(2,8,23,0.05);
        }

        .or-demo-avatar {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 800;
          flex-shrink: 0;
        }

        .or-demo-role {
          color: #111827;
          font-size: 14px;
          font-weight: 800;
          line-height: 1.3;
        }

        .or-demo-scope {
          margin-top: 4px;
          color: #6b7280;
          font-size: 11px;
          line-height: 1.4;
        }

        .or-highlights {
          margin-top: 20px;
          background: rgba(255,255,255,0.86);
          border: 1px solid rgba(255,255,255,0.82);
          backdrop-filter: blur(12px);
          box-shadow:
            0 12px 28px rgba(2,8,23,0.06),
            0 4px 10px rgba(2,8,23,0.03);
          border-radius: 24px;
          padding: 20px 20px 16px;
        }

        .or-highlights-title {
          color: #111827;
          font-size: 15px;
          font-weight: 800;
          margin-bottom: 14px;
        }

        .or-highlight-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 8px;
          border-radius: 14px;
          transition: background 0.15s ease;
        }

        .or-highlight-row:hover {
          background: rgba(249,250,251,0.88);
        }

        .or-highlight-icon {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
        }

        .or-highlight-copy {
          min-width: 0;
          flex: 1;
        }

        .or-highlight-head {
          color: #111827;
          font-size: 14px;
          font-weight: 800;
          line-height: 1.3;
        }

        .or-highlight-desc {
          margin-top: 3px;
          color: #6b7280;
          font-size: 12px;
          line-height: 1.4;
        }

        .or-highlight-arrow {
          color: #cbd5e1;
          font-size: 16px;
          flex-shrink: 0;
        }

        .or-copy {
          text-align: center;
          margin-top: 14px;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 600;
        }

        @keyframes orFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes orSpin {
          to { transform: rotate(360deg); }
        }

        .or-fade-1 { animation: orFadeUp 0.4s ease both; }
        .or-fade-2 { animation: orFadeUp 0.4s ease 0.05s both; }
        .or-fade-3 { animation: orFadeUp 0.4s ease 0.1s both; }
        .or-spin { animation: orSpin 0.8s linear infinite; }

        @media (min-width: 1100px) {
          .or-left { display: block; }
          .or-mobile-brand { display: none; }
          .or-login-brand { display: block; }
          .or-right { padding: 20px 24px; }
          .or-right-inner { max-width: 420px; }
        }

        @media (max-width: 1099px) {
          .or-right {
            padding: 24px 20px;
          }
          .or-right-inner {
            max-width: 480px;
          }
          .or-login-card {
            padding: 28px 24px 24px;
          }
          .or-highlights {
            padding: 20px 20px 16px;
          }
        }

        @media (max-width: 768px) {
          .or-right {
            padding: 20px 16px;
          }
          .or-right-inner {
            max-width: 100%;
          }
          .or-demo-grid {
            grid-template-columns: 1fr;
          }
          .or-login-title {
            font-size: 28px;
          }
          .or-hero h1 {
            font-size: 36px;
          }
        }
      `}</style>

      <div className="or-root">
        <aside className="or-left">
          <div className="or-left-glow" />
          <div className="or-left-inner">
            <div className="or-brand">
              <div className="or-brand-lock">🔒</div>
              <div className="or-brand-text">
                <div className="or-brand-title">Omni<span>Retail</span></div>
                <div className="or-brand-sub">Point of sale platform</div>
              </div>
            </div>

            <div className="or-chip">
              <span>Retail OS</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span>Built for modern operators</span>
            </div>

            <div className="or-hero">
              <h1>The complete retail operating system.</h1>
              <p>
                POS, inventory, purchasing, finance, HR, and eCommerce unified in one
                platform for teams that need operational speed, control, and visibility.
              </p>
            </div>

            <div className="or-stats">
              <div className="or-stat">
                <div className="or-stat-value">73</div>
                <div className="or-stat-label">Screens & modules</div>
              </div>
              <div className="or-stat">
                <div className="or-stat-value">300+</div>
                <div className="or-stat-label">API endpoints</div>
              </div>
              <div className="or-stat">
                <div className="or-stat-value">10+</div>
                <div className="or-stat-label">Integrations</div>
              </div>
              <div className="or-stat">
                <div className="or-stat-value">100%</div>
                <div className="or-stat-label">Multi-currency</div>
              </div>
            </div>

            <div className="or-feature-grid">
              {FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  className="or-feature"
                  style={{
                    background: feature.bg,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 1px ${feature.accent}16`,
                  }}
                >
                  <div className="or-feature-head">
                    <div
                      className="or-feature-icon"
                      style={{ boxShadow: `0 8px 18px ${feature.accent}20` }}
                    >
                      {feature.icon}
                    </div>
                    <div className="or-feature-arrow">→</div>
                  </div>
                  <div className="or-feature-title">{feature.title}</div>
                  <div className="or-feature-desc">{feature.desc}</div>
                </div>
              ))}
            </div>

            <div className="or-left-footer">
              <div className="or-footer-sec">
                <span style={{ color: '#34D399', display: 'inline-flex' }}>
                  <ShieldIcon />
                </span>
                <span>Enterprise-grade security and role-based access controls</span>
              </div>
              <div>v2.0</div>
            </div>
          </div>
        </aside>

        <main className="or-right">
          <div className="or-right-inner">
            <div className="or-mobile-brand or-fade-1">
              <div className="or-brand">
                <div className="or-brand-lock">🔒</div>
                <div className="or-brand-text">
                  <div className="or-brand-title">Omni<span>Retail</span></div>
                  <div className="or-brand-sub">Point of sale platform</div>
                </div>
              </div>
            </div>

            <section className="or-login-card or-fade-1">
              <div className="or-login-brand">
                <div className="or-brand">
                  <div className="or-brand-lock">🔒</div>
                  <div className="or-brand-text">
                    <div className="or-brand-title">Omni<span>Retail</span></div>
                    <div className="or-brand-sub">Point of sale platform</div>
                  </div>
                </div>
              </div>

              <div className="or-login-head">
                <h1 className="or-login-title">Sign in</h1>
                <p className="or-login-copy">
                  Access your retail workspace, dashboards, operations, and financial controls.
                </p>
              </div>

              {error && (
                <div className="or-error" role="alert" aria-live="polite">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={submit}>
                <div className="or-field">
                  <label className="or-label" htmlFor="email">
                    Email address
                  </label>
                  <div className="or-input-wrap">
                    <div className="or-input-icon">
                      <EmailIcon />
                    </div>
                    <input
                      id="email"
                      type="email"
                      className="or-input"
                      autoFocus
                      autoComplete="email"
                      value={email}
                      required
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@omniretail.local"
                    />
                  </div>
                </div>

                <div className="or-field">
                  <div className="or-label-row">
                    <label className="or-label" htmlFor="password">
                      Password
                    </label>
                    <button
                      type="button"
                      className="or-forgot"
                      onClick={() => toast('Forgot password flow not wired yet.')}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <div className="or-input-wrap">
                    <div className="or-input-icon">
                      <LockIcon />
                    </div>
                    <input
                      id="password"
                      type={showPw ? 'text' : 'password'}
                      className="or-input or-input-password"
                      autoComplete={remember ? 'current-password' : 'off'}
                      value={password}
                      required
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="or-eye"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showPw} />
                    </button>
                  </div>
                </div>

                <div className="or-remember-row">
                  <div
                    className={`or-check ${remember ? 'active' : ''}`}
                    role="checkbox"
                    aria-checked={remember}
                    tabIndex={0}
                    onClick={() => setRemember((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setRemember((v) => !v);
                      }
                    }}
                  >
                    <div className="or-check-box">
                      {remember && (
                        <svg width="12" height="10" viewBox="0 0 11 9" fill="none">
                          <path
                            d="M1 4.5L4 7.5L10 1.5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="or-check-label">Remember me</div>
                  </div>
                </div>

                <button type="submit" className="or-login-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <svg
                        className="or-spin"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 12a9 9 0 11-6.22-8.56" />
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <span>Sign in</span>
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>

              <div className="or-divider">
                <div className="or-divider-line" />
                <div className="or-divider-text">New here?</div>
                <div className="or-divider-line" />
              </div>

              <div>
                <button
                  type="button"
                  className="or-demo-card"
                  style={{ width: '100%', justifyContent: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    setEmail(DEMO_ACCOUNTS[0].email);
                    setPassword(DEMO_ACCOUNTS[0].password);
                    setError('');
                  }}
                >
                  <div style={{ minWidth: 0, textAlign: 'center' }}>
                    <div className="or-demo-role">Explore the demo</div>
                    <div className="or-demo-scope">Prefill the shared demo workspace</div>
                  </div>
                </button>
                <div style={{ marginTop: 12, textAlign: 'center', fontSize: 13, color: '#64748b' }}>
                  Want your own workspace?{' '}
                  <Link to="/register" style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>
                    Start a free trial
                  </Link>
                </div>
              </div>
            </section>

            <section className="or-highlights or-fade-2">
              <div className="or-highlights-title">Platform highlights</div>
              <div>
                {HIGHLIGHTS.map((item) => (
                  <div key={item.title} className="or-highlight-row">
                    <div
                      className="or-highlight-icon"
                      style={{
                        background: `${item.color}16`,
                        color: item.color,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div className="or-highlight-copy">
                      <div className="or-highlight-head">{item.title}</div>
                      <div className="or-highlight-desc">{item.desc}</div>
                    </div>
                    <div className="or-highlight-arrow">›</div>
                  </div>
                ))}
              </div>
            </section>

            <div className="or-copy or-fade-3">
              © 2026 OmniRetail · Retail operating system
            </div>
          </div>
        </main>
      </div>
    </>
  );
}